import { App, Button, Card, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { EnergyPack } from '@goelectric/shared-types';
import { useAuth } from '@/features/auth/context/AuthContext';
import { canWritePacks } from '@/layouts/navigation';
import { packApi } from '../api/packApi';
import { PackTable } from '../components/PackTable';
import { usePacks } from '../hooks/usePacks';
import { packErrorMessage } from '../utils/errors';
import { canFilterPackStatus } from '../utils/roles';

/**
 * Listado de Packs de Energía (US-2.4, `/packs`) — paginación server-side vía
 * `usePacks`, con búsqueda por nombre (debounce) y filtro por estado. Los roles de
 * plataforma ven `ALL`; ALIADO_ADMIN/CONTADOR quedan fijos en `ACTIVE` porque el
 * backend les restringe el estado y `validAt`. La escritura es solo `SUPER_ADMIN`.
 */
export function PacksListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { message } = App.useApp();
  const canFilterStatus = canFilterPackStatus(user?.role);
  const packs = usePacks({ statusScope: canFilterStatus ? 'all' : 'active-only' });

  const canWrite = canWritePacks(user?.role);
  const goToCreate = () => navigate('/packs/nuevo');

  const remove = async (pack: EnergyPack) => {
    try {
      await packApi.remove(pack.id);
      message.success('Pack eliminado.');
      // Si se dio de baja el último elemento de una página > 0, retrocede una página
      // para no dejar un empty-state engañoso.
      if (packs.data.length === 1 && packs.offset > 0) {
        packs.setOffset(Math.max(0, packs.offset - packs.limit));
      } else {
        packs.refresh();
      }
    } catch (caught) {
      message.error(packErrorMessage(caught, 'No se pudo eliminar el pack.'));
    }
  };

  return (
    <div className="packs-page">
      <div className="packs-page__header">
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Packs de energía
          </Typography.Title>
          <Typography.Text type="secondary">
            Gestiona las bolsas de kWh con vigencia que ofrece la plataforma.
          </Typography.Text>
        </div>
        {canWrite && (
          <Button type="primary" icon={<PlusOutlined />} onClick={goToCreate}>
            Nuevo pack
          </Button>
        )}
      </div>

      <Card>
        <PackTable
          packs={packs.data}
          meta={packs.meta}
          loading={packs.loading}
          error={packs.error}
          filters={packs.filters}
          canWrite={canWrite}
          canFilterStatus={canFilterStatus}
          onFiltersChange={packs.setFilters}
          onOffsetChange={packs.setOffset}
          onRetry={packs.refresh}
          onCreate={goToCreate}
          onSelect={(pack) => navigate(`/packs/${encodeURIComponent(pack.id)}`)}
          onEdit={(pack) => navigate(`/packs/${encodeURIComponent(pack.id)}/editar`)}
          onDelete={(pack) => void remove(pack)}
        />
      </Card>
    </div>
  );
}
